import {
  removeSensitiveUrlParameters,
  sanitizeUrlForAnalytics,
} from "@/lib/urlPrivacy";

const URL_FIELD_NAMES = new Set([
  "url",
  "uri",
  "href",
  "location",
  "pagelocation",
  "pagepath",
  "pagereferrer",
  "referrer",
  "referer",
  "landingurl",
  "sourceurl",
  "linkurl",
  "previous",
  "from",
  "to",
  "transaction",
]);

const compactFieldName = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const sanitizeRawQueryString = (value: string) => {
  const params = new URLSearchParams(value);
  removeSensitiveUrlParameters(params);
  return params.toString();
};

const hasSensitiveUrlParameter = (value: string) =>
  /(?:^|[?&#])(?:access[_-]?token|accessToken|agent[_-]?access[_-]?token|activation[_-]?(?:access[_-]?)?token|refresh[_-]?token|invite[_-]?token|token|code|otp)=/i.test(
    value,
  );

const looksLikeUrl = (value: string) =>
  /^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|\?|#)/i.test(value);

const shouldSanitizeString = (key: string, value: string) =>
  hasSensitiveUrlParameter(value) ||
  (URL_FIELD_NAMES.has(compactFieldName(key)) && looksLikeUrl(value));

const isPlainObject = (value: object) =>
  Object.prototype.toString.call(value) === "[object Object]";

const sanitizeNestedUrls = (value: unknown, seen: WeakSet<object>): unknown => {
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return value;

  if (Array.isArray(value)) {
    seen.add(value);
    return value.map((entry) => {
      try {
        return sanitizeNestedUrls(entry, seen);
      } catch {
        // Exotic nested value (getter-only property, host object, etc.) — keep original rather than drop the whole event.
        return entry;
      }
    });
  }

  if (!isPlainObject(value)) return value;
  seen.add(value);

  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  let entries: [string, unknown][];
  try {
    entries = Object.entries(record);
  } catch {
    return value;
  }
  entries.forEach(([key, entry]) => {
    try {
      if (typeof entry === "string" && compactFieldName(key) === "querystring") {
        result[key] = sanitizeRawQueryString(entry);
        return;
      }
      if (typeof entry === "string" && shouldSanitizeString(key, entry)) {
        result[key] = looksLikeUrl(entry)
          ? sanitizeUrlForAnalytics(entry)
          : sanitizeRawQueryString(entry);
        return;
      }
      result[key] = sanitizeNestedUrls(entry, seen);
    } catch {
      // Exotic nested value (getter-only property, host object, etc.) — keep original rather than drop the whole event.
      result[key] = entry;
    }
  });
  return result;
};

/** Scrubs URLs from Sentry errors, transactions, breadcrumbs, and Replay frames. */
export const sanitizeSentryEventUrls = <T>(event: T): T => {
  try {
    return sanitizeNestedUrls(event, new WeakSet()) as T;
  } catch {
    // Never let sanitization break the Sentry pipeline (or the app) — send the event as-is.
    return event;
  }
};
